import { goRequest } from '../utils/httpClientTs';
import {
  LocalChapterInfo,
  LocalChapterInfoUpdateRequest,
  LocalChapterTypeOption,
  MeetingTimesRequest,
  LocalChapterDetailResponse,
  SPDChapterInfoResponse,
  SPDChapterListResponse,
  LocalChapterListResponse,
  InternationalChapterInfoResponse,
  ElectionOfOfficersStatusResponse,
  ElectionSubmitRequest,
  ByLawsResponse,
} from '../types/chapters';
import {
  LocalChapterCommittee,
  LocalChapterCommitteePost,
  LocalChapterCommitteePut,
  LocalChapterCommitteeMemberPost,
  LocalChapterCommitteeMemberPut,
  LocalChapterCommitteeMemberReplace,
  LocalChapterCommitteePosition,
  LocalChapterGroup,
  SPDChapterCommittee,
  SPDChapterCommitteePost,
  SPDChapterCommitteePut,
  SPDChapterCommitteeMemberPost,
  SPDChapterCommitteeMemberPut,
  SPDChapterCommitteeMemberReplace,
  SPDChapterCommitteePosition,
  SPDChapterCommitteeType,
} from '../types/committee';
import { GenericResponse } from '../types/generic';

type LocalChapterRequest = {
  chapterId: string;
};

export type DisbandLocalChapterRequest = {
  disbanded_date: string;
};

export const getLocalChapterDetail = (
  chapterId: string
): Promise<LocalChapterDetailResponse> => {
  return goRequest<LocalChapterDetailResponse>({
    url: `/membership/local_chapters/${chapterId}`,
    method: 'GET',
  });
};

export const getLocalChapterInfo = (
  data: LocalChapterRequest
): Promise<LocalChapterInfo> => {
  return goRequest<LocalChapterInfo>({
    url: `/membership/local_chapters/${data.chapterId}/info`,
    method: 'GET',
  });
};

export const getLocalChapterTypes = (): Promise<LocalChapterTypeOption[]> => {
  return goRequest<LocalChapterTypeOption[]>({
    url: `/membership/local_chapters/types`,
    method: 'GET',
  });
};

export const updateLocalChapterInfo = (
  chapterId: string,
  data: LocalChapterInfoUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/info`,
    method: 'PATCH',
    data: data,
  });
};

export const disbandLocalChapter = (
  chapterId: string,
  data: { disbanded_date: string }
): Promise<LocalChapterInfo> => {
  return goRequest<LocalChapterInfo>({
    url: `/membership/local_chapters/${chapterId}/disband`,
    method: 'POST',
    data,
  });
};

export const postLocalChapterMeetingTimes = (
  chapterId: string,
  data: MeetingTimesRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/meeting_times`,
    method: 'POST',
    data: data,
  });
};

export const patchLocalChapterMeetingTimes = (
  chapterId: string,
  meetingId: string,
  data: MeetingTimesRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/meeting_times/${meetingId}`,
    method: 'PATCH',
    data: data,
  });
};

export const deleteLocalChapterMeetingTimes = (
  chapterId: string,
  meetingId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/meeting_times/${meetingId}`,
    method: 'DELETE',
  });
};

// Chapter bylaws / standing rules (?type=by_laws|standing_rules)
export type ChapterByLawsType = 'by_laws' | 'standing_rules';

export const getChapterBylaws = (
  chapterId: string,
  type: ChapterByLawsType = 'by_laws'
): Promise<ByLawsResponse> => {
  return goRequest<ByLawsResponse>({
    url: `/membership/local_chapters/${chapterId}/chapter_by_laws`,
    method: 'GET',
    params: { type },
  });
};

export const createOrUpdateChapterBylaws = (
  chapterId: string,
  body: ByLawsResponse,
  type: ChapterByLawsType = 'by_laws'
): Promise<ByLawsResponse> => {
  return goRequest<ByLawsResponse>({
    url: `/membership/local_chapters/${chapterId}/chapter_by_laws`,
    method: 'PUT',
    params: { type },
    data: body,
  });
};

export const deleteChapterBylaws = (
  chapterId: string,
  type: ChapterByLawsType = 'by_laws'
): Promise<void> => {
  return goRequest<void>({
    url: `/membership/local_chapters/${chapterId}/chapter_by_laws`,
    method: 'DELETE',
    params: { type },
  });
};

// Local Chapter Committee Endpoints

export const getLocalChapterCommittees = (
  chapterId: string
): Promise<LocalChapterCommittee[]> => {
  return goRequest<LocalChapterCommittee[]>({
    url: `/membership/local_chapters/${chapterId}/committees/full`,
    method: 'GET',
  });
};

export const createLocalChapterCommittee = (
  chapterId: string,
  data: LocalChapterCommitteePost
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/groups`,
    method: 'POST',
    data: data,
  });
};

export const updateLocalChapterCommittee = (
  committeeId: string,
  data: LocalChapterCommitteePut
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${committeeId}`,
    method: 'PUT',
    data: data,
  });
};

export const getLocalChapterCommitteePositions = (): Promise<
  LocalChapterCommitteePosition[]
> => {
  return goRequest<LocalChapterCommitteePosition[]>({
    url: `/membership/committees/local_chapter_positions`,
    method: 'GET',
  });
};

// Local Chapter Committee Member Endpoints

export const addLocalChapterCommitteeMember = (
  committeeId: string,
  data: LocalChapterCommitteeMemberPost
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${committeeId}/members`,
    method: 'POST',
    data: data,
  });
};

export const updateLocalChapterCommitteeMember = (
  committeeId: string,
  memberId: string,
  data: LocalChapterCommitteeMemberPut
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${committeeId}/members/${memberId}`,
    method: 'PUT',
    data: data,
  });
};

export const replaceLocalChapterCommitteeMember = (
  committeeId: string,
  memberId: string,
  data: LocalChapterCommitteeMemberReplace
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${committeeId}/members/${memberId}/replace`,
    method: 'POST',
    data: data,
  });
};

export const deleteLocalChapterCommitteeMember = (
  committeeId: string,
  memberId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${committeeId}/members/${memberId}`,
    method: 'DELETE',
  });
};

// Local Chapter Group Endpoints

export const getLocalChapterGroups = (
  chapterId: string
): Promise<LocalChapterGroup[]> => {
  return goRequest<LocalChapterGroup[]>({
    url: `/membership/local_chapters/${chapterId}/groups`,
    method: 'GET',
  });
};

export const createLocalChapterGroup = (
  chapterId: string,
  data: LocalChapterCommitteePost
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/groups`,
    method: 'POST',
    data: data,
  });
};

export const updateLocalChapterGroup = (
  groupId: string,
  data: LocalChapterCommitteePut
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/groups/${groupId}`,
    method: 'PUT',
    data: data,
  });
};

// SPD Chapter Committee Endpoints

export const getSPDChapterCommittees = (
  chapterId: string
): Promise<SPDChapterCommittee[]> => {
  return goRequest<SPDChapterCommittee[]>({
    url: `/membership/spd_chapters/${chapterId}/committees`,
    method: 'GET',
  });
};

export const getSPDChapterCommitteeTypes = (): Promise<
  SPDChapterCommitteeType[]
> => {
  return goRequest<SPDChapterCommitteeType[]>({
    url: `/membership/spd_chapters/committees_types`,
    method: 'GET',
  });
};

export const createSPDChapterCommittee = (
  chapterId: string,
  data: SPDChapterCommitteePost
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/committees`,
    method: 'POST',
    data: data,
  });
};

export const updateSPDChapterCommittee = (
  chapterId: string,
  committeeId: string,
  data: SPDChapterCommitteePut
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/committees/${committeeId}`,
    method: 'PUT',
    data: data,
  });
};

export const addSPDChapterCommitteeMember = (
  chapterId: string,
  committeeId: string,
  data: SPDChapterCommitteeMemberPost
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/committees/${committeeId}/members`,
    method: 'POST',
    data: data,
  });
};

export const updateSPDChapterCommitteeMember = (
  chapterId: string,
  memberId: string,
  data: SPDChapterCommitteeMemberPut
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/members/${memberId}`,
    method: 'PUT',
    data: data,
  });
};

export const replaceSPDChapterCommitteeMember = (
  chapterId: string,
  memberId: string,
  data: SPDChapterCommitteeMemberReplace
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/members/${memberId}/replace`,
    method: 'POST',
    data: data,
  });
};

export const deleteSPDChapterCommitteeMember = (
  chapterId: string,
  memberId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/members/${memberId}`,
    method: 'DELETE',
  });
};

export const getSPDChapterCommitteePositions = (
  chapterId: string,
  committeeId: string
): Promise<SPDChapterCommitteePosition[]> => {
  return goRequest<SPDChapterCommitteePosition[]>({
    url: `/membership/spd_chapters/${chapterId}/committees/${committeeId}/roles`,
    method: 'GET',
  });
};

// SPD Chapter Info Endpoints

export const getSPDChapterInfo = (data: {
  id: string;
}): Promise<SPDChapterInfoResponse> => {
  return goRequest<SPDChapterInfoResponse>({
    url: `/membership/spd_chapters/${data.id}/info`,
    method: 'GET',
  });
};

export const updateSPDChapterInfo = (
  chapterId: string,
  data: {
    name?: string;
    chapter_type_id?: string;
    region_id?: string;
    sponsor_id?: string | null;
    international_representative_id?: string | null;
    this_years_meeting_date?: string | null;
  }
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${chapterId}/info`,
    method: 'PATCH',
    data: data,
  });
};

export const getSPDChapterTypes = (): Promise<
  Array<{ id: string; name: string }>
> => {
  return goRequest<Array<{ id: string; name: string }>>({
    url: `/membership/spd_chapters/chapter_types`,
    method: 'GET',
  });
};

export const getSPDRegions = (): Promise<
  Array<{ id: string; name: string }>
> => {
  return goRequest<Array<{ id: string; name: string }>>({
    url: `/membership/spd_chapters/regions`,
    method: 'GET',
  });
};

export const getSPDSponsors = (): Promise<
  Array<{ id: string; name: string }>
> => {
  return goRequest<Array<{ id: string; name: string }>>({
    url: `/membership/spd_chapters/sponsors`,
    method: 'GET',
  });
};

export const getSPDChaptersList = (): Promise<SPDChapterListResponse[]> => {
  return goRequest<SPDChapterListResponse[]>({
    url: `/membership/spd_chapters`,
    method: 'GET',
  });
};

export const getLCChaptersList = (data: {
  rowsPerPage: number;
  page: number;
  spd?: string;
  status?: string;
  search?: string;
  orderBy?: string;
  order?: string;
}): Promise<LocalChapterListResponse> => {
  let url = `/membership/local_chapters?per_page=${data.rowsPerPage}&page=${data.page}`;
  if (data.spd) {
    url += `&spd_chapter_id=${data.spd}`;
  }
  if (data.status && data.status !== 'all') {
    url += `&status=${data.status}`;
  }
  if (data.search) {
    url += `&search=${data.search}`;
  }
  if (data.orderBy) {
    url += `&order_by=${data.orderBy}`;
  }
  if (data.order) {
    url += `&order=${data.order}`;
  }
  return goRequest<LocalChapterListResponse>({
    url: url,
    method: 'GET',
  });
};

export const getIntlInfo = (): Promise<InternationalChapterInfoResponse> => {
  return goRequest<InternationalChapterInfoResponse>({
    url: `/membership/international/info`,
    method: 'GET',
  });
};

// Local Chapter Leadership Endpoints

export interface LCLeadershipData {
  officers: Array<{
    id: string;
    position: string;
    position_id: string;
    committee_id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    user_id: string;
    order: number;
  }>;
  project_chairs: Array<{
    id: string;
    position: string;
    position_id: string;
    committee_id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    user_id: string;
  }>;
  delegates?: Array<{
    id: string;
    user_id: string;
    user_name: string;
    delegate_type: 'delegate' | 'alternate';
    order: number;
  }>;
  alternates?: Array<{
    id: string;
    user_id: string;
    user_name: string;
    delegate_type: 'delegate' | 'alternate';
    order: number;
  }>;
  delegate_reporting?: {
    id: string;
    number_of_delegates: number;
    reported_on?: string;
    reported_by_id?: string;
    updated_on?: string;
    updated_by_id?: string;
    can_report: boolean;
  };
}

export interface LCLeadershipRequest {
  formId: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
  position_id: string;
  committee_id: string;
}

export interface LCLeadershipUpdateRequest {
  formId: string;
  id: string;
  start_date: string;
  end_date?: string | null;
}

export interface LCLeadershipReplaceRequest {
  formId: string;
  id: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface LCLeadershipDeleteRequest {
  formId: string;
  id: string;
}

export interface RoleHistoryMember {
  user_id: string;
  name: string;
  start_date: string;
  end_date?: string | null;
}

export interface RoleHistoryResponse {
  future: RoleHistoryMember[];
  past: RoleHistoryMember[];
}

export const getLCRoleHistory = (
  chapterId: string,
  positionId: string
): Promise<RoleHistoryResponse> => {
  return goRequest<RoleHistoryResponse>({
    url: `/membership/local_chapters/${chapterId}/leadership/role-history?position_id=${encodeURIComponent(positionId)}`,
    method: 'GET',
  });
};

export const getLCLeadership = (data: {
  id: string;
}): Promise<LCLeadershipData> => {
  return goRequest<LCLeadershipData>({
    url: `/membership/local_chapters/${data.id}/leadership`,
    method: 'GET',
  });
};

export const patchLCLeadership = (
  data: LCLeadershipUpdateRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const { formId: _, ...updateData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${formId}/leadership`,
    method: 'PATCH',
    data: updateData,
  });
};

export const postLCLeadership = (
  data: LCLeadershipRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const { formId: _, ...postData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${formId}/leadership`,
    method: 'POST',
    data: postData,
  });
};

export const replaceExistingLCLeadership = (
  data: LCLeadershipReplaceRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const leadershipId = data.id;
  const { formId: _, id: __, ...replaceData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${formId}/leadership/${leadershipId}`,
    method: 'POST',
    data: replaceData,
  });
};

export const deleteLCLeadership = (
  data: LCLeadershipDeleteRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${data.formId}/leadership/${data.id}`,
    method: 'DELETE',
  });
};

// Election of officers (annual report workflow)
export const getElectionOfOfficersStatus = (
  chapterId: string
): Promise<ElectionOfOfficersStatusResponse> => {
  return goRequest<ElectionOfOfficersStatusResponse>({
    url: `/membership/local_chapters/${chapterId}/election-of-officers/status`,
    method: 'GET',
  });
};

export const submitElectionOfOfficers = (
  chapterId: string,
  data: ElectionSubmitRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/local_chapters/${chapterId}/election-of-officers/submit`,
    method: 'POST',
    data,
  });
};

// SPD Chapter Detail and Leadership Endpoints

export interface SPDLeadershipData {
  /** True when the user has spd:edit for this SPD (from API). */
  can_edit?: boolean;
  officers: Array<{
    id: string;
    position: string;
    position_id: string;
    committee_id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    user_id: string;
    order: number;
  }>;
  assistants: Array<{
    id: string;
    position: string;
    position_id: string;
    committee_id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    user_id: string;
    order: number;
  }>;
  qbe_users: Array<{
    id: string;
    position: string;
    position_id: string;
    committee_id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    user_id: string;
    order: number;
  }>;
}

export interface SPDLeadershipRequest {
  formId: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
  position_id: string;
  committee_id: string;
}

export interface SPDLeadershipUpdateRequest {
  formId: string;
  id: string;
  start_date: string;
  end_date?: string | null;
}

export interface SPDLeadershipReplaceRequest {
  formId: string;
  id: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface SPDLeadershipDeleteRequest {
  formId: string;
  id: string;
}

export const getSpdDetail = (data: { id: string }): Promise<any> => {
  return goRequest<any>({
    url: `/membership/spd_chapters/${data.id}`,
    method: 'GET',
  });
};

export const getSPDLeadership = (data: {
  id: string;
}): Promise<SPDLeadershipData> => {
  return goRequest<SPDLeadershipData>({
    url: `/membership/spd_chapters/${data.id}/leadership`,
    method: 'GET',
  });
};

export const getSPDLeadershipPositions = (): Promise<any> => {
  return goRequest<any>({
    url: `/membership/spd_chapters/assistant_positions`,
    method: 'GET',
  });
};

export const patchSPDLeadership = (
  data: SPDLeadershipUpdateRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const positionId = data.id;
  const { formId: _, ...updateData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/leadership/${positionId}`,
    method: 'PATCH',
    data: updateData,
  });
};

export const postSPDLeadership = (
  data: SPDLeadershipRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const { formId: _, ...postData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/leadership`,
    method: 'POST',
    data: postData,
  });
};

export const replaceExistingLeadership = (
  data: SPDLeadershipReplaceRequest
): Promise<GenericResponse> => {
  const formId = data.formId;
  const leadershipId = data.id;
  const { formId: _, id: __, ...replaceData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/leadership/${leadershipId}`,
    method: 'POST',
    data: replaceData,
  });
};

export const deleteSPDLeadership = (
  data: SPDLeadershipDeleteRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${data.formId}/leadership/${data.id}`,
    method: 'DELETE',
  });
};

// International (SPD) Committee Endpoints

export const getSPDCommittees = (data: { id: string }): Promise<any> => {
  return goRequest<any>({
    url: `/membership/spd_chapters/${data.id}/committees`,
    method: 'GET',
  });
};

export const patchSPDCommittees = (data: {
  formId: string;
  id: string;
  start_date: string;
  end_date?: string | null;
}): Promise<GenericResponse> => {
  const formId = data.formId;
  const { formId: _, ...updateData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/committees`,
    method: 'PATCH',
    data: updateData,
  });
};

export const postSPDCommittees = (data: {
  formId: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
  position_id: string;
  committee_id: string;
}): Promise<GenericResponse> => {
  const formId = data.formId;
  const { formId: _, ...postData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/committees`,
    method: 'POST',
    data: postData,
  });
};

export const replaceExistingCommittees = (data: {
  formId: string;
  id: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
}): Promise<GenericResponse> => {
  const formId = data.formId;
  const leadershipId = data.id;
  const { formId: _, id: __, ...replaceData } = data;
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${formId}/committees/${leadershipId}`,
    method: 'POST',
    data: replaceData,
  });
};

export const deleteSPDCommittees = (data: {
  formId: string;
  id: string;
}): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/membership/spd_chapters/${data.formId}/committees/${data.id}`,
    method: 'DELETE',
  });
};

// Local Chapter Members Endpoint

export const getLCMembers = (data: {
  id: string;
  userType?: string;
  dateSince?: any;
}): Promise<any> => {
  let url = `/membership/local_chapters/${data.id}/members`;
  if (data.userType) {
    url += `?type=${data.userType}`;
  }
  if (data.dateSince) {
    url += `&date_since=${data.dateSince.format('YYYY-MM-DD')}`;
  }
  return goRequest<any>({
    url: url,
    method: 'GET',
  });
};

// Overseas Contacts Endpoint

export type IntlLeadershipMember = {
  id: string;
  user_id: string;
  name: string;
  group_id?: string;
  committee_id?: string;
  role?: string;
  role_id?: string;
  position?: string;
  position_id?: string;
  start_date: string;
  end_date: string | null;
  order: number;
};

export type OverseasContactsResponse = {
  can_edit: boolean;
  data: Array<{
    id: string;
    name: string;
    members: IntlLeadershipMember[];
  }>;
};

export type StandingCommitteesResponse = {
  can_edit: boolean;
  data: Array<{
    id: string;
    name: string;
    description: string;
    is_project: boolean;
    members: IntlLeadershipMember[];
  }>;
};

export type StandingAppointmentsResponse = {
  can_edit: boolean;
  data: Array<{
    id: string;
    name: string;
    description: string;
    members: IntlLeadershipMember[];
  }>;
};

export type BoardOfDirectorsResponse = {
  id: string;
  name: string;
  description: string;
  can_edit: boolean;
  members: IntlLeadershipMember[];
};

export const getOverseasContacts = (): Promise<OverseasContactsResponse> => {
  return goRequest<OverseasContactsResponse>({
    url: '/membership/international/overseas_contacts',
    method: 'GET',
  });
};

export const addInternationalGroupMember = (params: {
  groupId: string;
  user_id: string;
  position_id: string;
  start_date: string;
  end_date?: string | null;
}): Promise<{ message: string }> => {
  return goRequest({
    url: `/membership/international/groups/${params.groupId}/members`,
    method: 'POST',
    data: {
      user_id: params.user_id,
      position_id: params.position_id,
      start_date: params.start_date,
      end_date: params.end_date || undefined,
    },
  });
};

export const updateInternationalGroupMember = (params: {
  groupId: string;
  memberId: string;
  start_date?: string;
  end_date?: string | null;
}): Promise<{ message: string }> => {
  return goRequest({
    url: `/membership/international/groups/${params.groupId}/members/${params.memberId}`,
    method: 'PUT',
    data: {
      start_date: params.start_date,
      end_date: params.end_date,
    },
  });
};

export const deleteInternationalGroupMember = (params: {
  groupId: string;
  memberId: string;
}): Promise<{ message: string }> => {
  return goRequest({
    url: `/membership/international/groups/${params.groupId}/members/${params.memberId}`,
    method: 'DELETE',
  });
};

export const replaceInternationalGroupMember = (params: {
  groupId: string;
  memberId: string;
  user_id: string;
  start_date: string;
  end_date?: string | null;
}): Promise<{ message: string }> => {
  return goRequest({
    url: `/membership/international/groups/${params.groupId}/members/${params.memberId}/replace`,
    method: 'POST',
    data: {
      user_id: params.user_id,
      start_date: params.start_date,
      end_date: params.end_date || undefined,
    },
  });
};
